/* Altruan Spar-Abo Widget
 * -----------------------------------------------------------------
 * Steuert den nativen Shopify-Parameter `selling_plan` im
 * Produktformular. Kein Bezug zum Seal-Widget-JS: Seal übernimmt die
 * Abos automatisch nach dem Checkout (Selling Plans sind native
 * Shopify-Funktionalität).
 *
 * Das Markup wird vollständig serverseitig in
 * snippets/altruan-abo-widget.liquid gerendert – dieses Script
 * ergänzt nur Interaktivität. Ohne JS bleibt die Seite als
 * Einmalkauf voll funktionsfähig.
 *
 * Übersetzbare Texte kommen aus den Theme-Locale-Dateien und werden
 * vom Liquid-Snippet ins Daten-JSON gerendert ("strings").
 *
 * Alle Preise im Widget sind Gesamtpreise für die gewählte Menge.
 * Der Einmalkauf ist die Summe aus dem Preisblock (price-slider.liquid).
 * Der Warenkorb rechnet den Staffelrabatt zusätzlich auf den Abo-Preis:
 * Abo-Stückpreis × Menge, Rabatt einmal auf die Zeile gerundet. Genau
 * so rechnet das Widget, sobald die Staffel den Preisblock vollständig
 * erklärt. Sonst (MHD-Abverkauf, Sonderpreis) skaliert es die Summe des
 * Preisblocks mit dem Verhältnis Plan-Preis / Variantenpreis.
 */
(() => {
  "use strict";

  if (customElements.get("altruan-abo-widget")) return;

  class AltruanAboWidget extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector("script[data-abo-data]");
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (_) {
        return;
      }

      this.fmt = new Intl.NumberFormat(this.data.locale || "de", {
        style: "currency",
        currency: this.data.currency || "EUR",
      });

      this.strings = this.data.strings || {};
      this.mode = this.dataset.defaultMode === "subscription" ? "subscription" : "one_time";

      this.form = this.findProductForm();
      this.planInput = this.ensureSellingPlanInput();

      this.addEventListener("click", (event) => {
        const tile = event.target.closest("[data-abo-mode]");
        if (tile) {
          this.mode = tile.dataset.aboMode === "subscription" ? "subscription" : "one_time";
          this.apply();
        }
      });

      this.addEventListener("change", (event) => {
        if (event.target.matches("[data-abo-interval-select]")) this.apply();
      });

      this.watchVariantChanges();
      this.watchPriceBlock();
      this.apply();
    }

    disconnectedCallback() {
      if (this.variantObserver) this.variantObserver.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
    }

    /* Das zugehörige Produktformular: erst innerhalb der eigenen
       Section suchen, dann seitenweit (Fallback).
       Wichtig: Dawn/Ignite rendert zusätzlich ein verstecktes
       Shop-Pay-Installment-Formular mit action="/cart/add" – wir
       brauchen das ECHTE Kauf-Formular (mit Kaufen-Button bzw. im
       <product-form>-Element). */
    findProductForm() {
      const scopes = [this.closest(".shopify-section"), document];
      for (const scope of scopes) {
        if (!scope) continue;
        const forms = [...scope.querySelectorAll('form[action*="/cart/add"]')].filter((f) =>
          f.querySelector('[name="id"]'),
        );
        const buyForm = forms.find((f) => f.querySelector('[name="add"]') || f.closest("product-form"));
        if (buyForm) return buyForm;
        if (forms.length) return forms[0];
      }
      return null;
    }

    /* Hidden Input für den Selling Plan anlegen (oder vorhandenen
       wiederverwenden). Bei Einmalkauf wird er `disabled`, damit er
       nicht mitserialisiert wird. */
    ensureSellingPlanInput() {
      if (!this.form) return null;
      let input = this.form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "selling_plan";
        input.disabled = true;
        this.form.appendChild(input);
      }
      return input;
    }

    /* Variantenwechsel erkennen: Ignite/Dawn aktualisiert das
       versteckte [name="id"]-Input. Change-Events plus
       MutationObserver decken beide Update-Wege ab. */
    watchVariantChanges() {
      if (!this.form) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput) return;

      this.form.addEventListener("change", () => {
        queueMicrotask(() => this.apply());
      });

      this.variantObserver = new MutationObserver(() => this.apply());
      this.variantObserver.observe(idInput, { attributes: true, attributeFilter: ["value"] });
    }

    /* Mengen- und Staffeländerungen schreiben die Summe in den
       Preisblock; beim Variantenwechsel wird er neu gerendert. Ein
       Observer auf der Section deckt beides ab. Eigene Änderungen am
       Widget werden ignoriert, sonst entstünde eine Schleife. */
    watchPriceBlock() {
      const root = this.closest(".shopify-section") || document.body;
      let queued = false;
      this.priceObserver = new MutationObserver((mutations) => {
        if (queued || mutations.every((m) => this.contains(m.target))) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          if (this.lineTotal(this.currentVariant()) !== this.lastTotal) this.apply();
        });
      });
      this.priceObserver.observe(root, { childList: true, subtree: true, characterData: true });
    }

    quantity() {
      const input = this.form && this.form.querySelector('[name="quantity"]');
      const fallback = document.querySelector('.quantity__input[name="quantity"]');
      return Math.max(1, parseInt((input || fallback)?.value, 10) || 1);
    }

    /* Netto-Summe des Einmalkaufs in Cent, wie sie der Preisblock
       anzeigt. Passt der Block nicht zur aktuellen Variante (Wechsel
       noch nicht gerendert) oder fehlt er: Menge × Variantenpreis. */
    lineTotal(variant) {
      if (!variant) return 0;
      const container = document.querySelector(".product-price-container");
      const netEl = container && container.querySelector('[data-role="net-price"]');
      const currentId = this.form && this.form.querySelector('[name="id"]')?.value;
      if (netEl && (!currentId || container.dataset.variantId === String(currentId))) {
        const cents = this.parseMoney(netEl.textContent);
        if (cents > 0) return cents;
      }
      return variant.price * this.quantity();
    }

    /* "12.201,69 €" → 1220169. Mit zwei Nachkommastellen sind die
       Ziffern direkt Cent, ohne (z. B. HUF) Einheiten × 100. */
    parseMoney(text) {
      const body = String(text || "")
        .replace(/[^\d.,'\u00A0 ]/g, "")
        .trim();
      const digits = body.replace(/\D/g, "");
      if (!digits) return 0;
      const value = parseInt(digits, 10);
      return /[.,'\u00A0 ]\d{2}$/.test(body) ? value : value * 100;
    }

    /* Staffel-Prozent für die Menge (aus product-slider.liquid), 0 ohne Slider. */
    staffelPct(qty) {
      const staffel = window.AltruanStaffel;
      return staffel && typeof staffel.discountFor === "function" ? staffel.discountFor(qty) : 0;
    }

    /* Netto-Zeilensumme in Cent wie der Staffelrabatt im Warenkorb:
       Reduktion über die ganze Zeile, einmal kaufmännisch gerundet.
       Gleiche Rechnung wie getLineNetTotal() in product-slider.liquid. */
    staffelLine(unitCents, qty, pct) {
      const list = unitCents * qty;
      if (!(pct > 0)) return list;
      const scale = 1000000000000n;
      const numerator = BigInt(Math.round(pct * 10000000000));
      const reduction = (BigInt(unitCents) * BigInt(qty) * numerator + scale / 2n) / scale;
      return list - Number(reduction);
    }

    currentVariant() {
      const id = this.form && this.form.querySelector('[name="id"]')?.value;
      return this.data.variants[id] || Object.values(this.data.variants)[0];
    }

    selectedPlanId() {
      const select = this.querySelector("[data-abo-interval-select]");
      return select && select.value ? select.value : null;
    }

    money(cents) {
      return this.fmt.format(cents / 100);
    }

    /* Brutto- und Netto-Wert nebeneinander; sichtbar ist je nach
       Brutto/Netto-Schalter nur einer (siehe price-mode-head). */
    moneyDual(cents) {
      const rate = (window.AltruanPriceMode && window.AltruanPriceMode.rate) || 1;
      return (
        `<span class="p-gross">${this.money(Math.round(cents * rate))}</span>` +
        `<span class="p-net">${this.money(cents)}</span>`
      );
    }

    /* Zentrale Update-Routine: Kachel-Zustände, Selling-Plan-Input
       und alle Preisanzeigen. */
    apply() {
      // Falls das Theme das Formular neu gerendert hat (z. B. beim
      // Variantenwechsel), Formular und Hidden Input neu verdrahten.
      if (!this.form || !this.form.isConnected) {
        this.form = this.findProductForm();
        this.planInput = this.ensureSellingPlanInput();
      }

      const variant = this.currentVariant();
      if (!variant) return;

      const isSub = this.mode === "subscription";
      for (const tile of this.querySelectorAll("[data-abo-mode]")) {
        const active = tile.dataset.aboMode === this.mode;
        tile.classList.toggle("is-active", active);
        tile.setAttribute("aria-pressed", String(active));
      }

      const intervals = this.querySelector("[data-abo-intervals]");
      const summary = this.querySelector("[data-abo-summary]");
      if (intervals) intervals.hidden = !isSub;
      if (summary) summary.hidden = !isSub;
      for (const benefit of this.querySelectorAll("[data-abo-benefit-more]")) benefit.hidden = !isSub;

      const planId = this.selectedPlanId();
      const plan = isSub && planId ? variant.plans[planId] : null;

      if (this.planInput) {
        this.planInput.disabled = !plan;
        this.planInput.value = plan ? planId : "";
      }

      // Gesamtpreise für die gewählte Menge (Einmalkauf-Summe × Planfaktor)
      const total = this.lineTotal(variant);
      this.lastTotal = total;
      const qty = this.quantity();
      const pct = this.staffelPct(qty);
      const exact = this.staffelLine(variant.price, qty, pct) === total;
      const scaled = (cents) => {
        if (exact) return this.staffelLine(cents, qty, pct);
        return variant.price > 0 ? Math.round((total * cents) / variant.price) : cents;
      };

      const oneTimeEl = this.querySelector("[data-abo-onetime-price]");
      if (oneTimeEl) oneTimeEl.innerHTML = this.moneyDual(total);

      const subPriceEl = this.querySelector("[data-abo-sub-price]");
      const recurringPrices = Object.values(variant.plans).map((p) => p.recurring);
      if (subPriceEl && recurringPrices.length) {
        const fromPrefix = this.strings.fromPrefix || "ab";
        subPriceEl.innerHTML = `${fromPrefix} ${this.moneyDual(scaled(Math.min(...recurringPrices)))}`;
      }

      if (plan) {
        const firstEl = this.querySelector("[data-abo-price-first]");
        const firstLabelEl = this.querySelector("[data-abo-label-first]");
        const recurringEl = this.querySelector("[data-abo-price-recurring]");
        const recurringLine = this.querySelector("[data-abo-line-recurring]");
        const samePrice = plan.first === plan.recurring;

        if (firstEl) firstEl.innerHTML = this.moneyDual(scaled(plan.first));
        if (firstLabelEl) {
          firstLabelEl.textContent = samePrice
            ? this.strings.perDelivery || "Pro Lieferung"
            : this.strings.firstDelivery || "1. Lieferung";
        }
        if (recurringEl) recurringEl.innerHTML = this.moneyDual(scaled(plan.recurring));
        if (recurringLine) recurringLine.hidden = samePrice;

        // Ersparnis-Badges und Durchstreichpreise live berechnen
        const base = variant.price;
        const pctFirst = base > 0 ? Math.round(((base - plan.first) / base) * 100) : 0;
        const pctRecurring = base > 0 ? Math.round(((base - plan.recurring) / base) * 100) : 0;
        const pairs = [
          ["[data-abo-save-first]", "[data-abo-compare-first]", pctFirst],
          ["[data-abo-save-recurring]", "[data-abo-compare-recurring]", pctRecurring],
        ];
        for (const [saveSel, compareSel, pct] of pairs) {
          const saveEl = this.querySelector(saveSel);
          const compareEl = this.querySelector(compareSel);
          if (saveEl) {
            saveEl.textContent = `−${pct}%`;
            saveEl.hidden = pct <= 0;
          }
          if (compareEl) {
            compareEl.innerHTML = this.moneyDual(total);
            compareEl.hidden = pct <= 0;
          }
        }
      }

      this.dispatchEvent(
        new CustomEvent("altruan-abo:change", {
          bubbles: true,
          detail: {
            mode: this.mode,
            sellingPlanId: plan ? Number(planId) : null,
          },
        }),
      );
    }
  }

  customElements.define("altruan-abo-widget", AltruanAboWidget);
})();
